import { useGomakeAxios, useGomakeRouter } from "@/hooks";
import {
  updateTokenStorage,
  updateRefreshTokenStorage,
} from "@/services/storage-data";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSetRecoilState } from "recoil";
import { companyProfileState } from "@/store/company-profile";
import { useTranslation } from "react-i18next";
import useTermsFlag from "@/hooks/use-terms"; // import the terms hook
import { tokenExpiredState } from "@/store/token-expired-modal";
import {
  isLocalDomainFeatureEnabled,
  LOCAL_DOMAIN_STORAGE_KEY,
} from "@/utils/local-domain";
import config from "@/config";
import { resetLoginRecaptcha } from "./login-recaptcha";

const LOCKOUT_MESSAGE =
  "Too many failed login attempts. Please try again later.";

const DEFAULT_LOGIN_REDIRECT = "/home";

const getSafeLoginRedirect = (redirectPath: string | null) => {
  if (!redirectPath || !redirectPath.startsWith("/")) {
    return DEFAULT_LOGIN_REDIRECT;
  }

  if (
    redirectPath.startsWith("//") ||
    redirectPath.startsWith("/login") ||
    redirectPath.startsWith("/404") ||
    redirectPath.startsWith("/no-print-house")
  ) {
    return DEFAULT_LOGIN_REDIRECT;
  }

  return redirectPath;
};

const useGomakeLogin = () => {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();
  const setUserProfile = useSetRecoilState(companyProfileState);
  const passwordRef = useRef<HTMLInputElement>(null);
  const isLocalhost = useMemo(() => isLocalDomainFeatureEnabled(), []);
  const [localDomain, setLocalDomain] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(LOCAL_DOMAIN_STORAGE_KEY) || "";
  });
  const passwordDefaultValue = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("password") ?? "";
  }, []);
  const [state, setState] = useState<{ username: string }>({ username: "" });
  const { isModalOpen, setIsModalOpen, setIsTermsAccepted } = useTermsFlag();
  const showLoginModal = useSetRecoilState(tokenExpiredState);

  const [errorMsg, setErrorMsg] = useState("");
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaTokenRef = useRef("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [name: string]: boolean }>({
    username: false,
    password: false,
  });

  const changeState = useCallback((key: string, value: any) => {
    if (key === "password") return;
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const changeLocalDomain = useCallback((domain: string) => {
    setLocalDomain(domain);
  }, []);

  const handleApplyDomain = useCallback(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(LOCAL_DOMAIN_STORAGE_KEY, localDomain.trim());
    window.location.reload();
  }, [localDomain]);

  const handleCaptchaChange = useCallback((token: string) => {
    captchaTokenRef.current = token;
    setCaptchaToken(token);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    captchaTokenRef.current = "";
    setCaptchaToken("");
  }, []);

  const resetCaptchaState = useCallback((clearRequired = false) => {
    captchaTokenRef.current = "";
    setCaptchaToken("");
    if (clearRequired) {
      setRequiresCaptcha(false);
    }
    resetLoginRecaptcha();
    setCaptchaKey((key) => key + 1);
  }, []);

  const onClickLogin = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    const passwordValue = (passwordRef.current?.value ?? "").trimStart();
    const currentCaptchaToken = captchaTokenRef.current;
    if (state?.username?.length <= 0) {
      setErrors({ username: true, password: false });
      setErrorMsg(t("login.emailIsRequired"));
      resetCaptchaState();
    } else if (passwordValue.length <= 0) {
      setErrors({ username: false, password: true });
      setErrorMsg(t("login.passwordIsRequired"));
      resetCaptchaState();
    } else if (
      requiresCaptcha &&
      config.recaptcha_site_key &&
      !currentCaptchaToken
    ) {
      setErrors({ username: false, password: false });
      setErrorMsg(t("login.captchaRequired"));
    } else {
      setErrors({ username: false, password: false });
      setIsSubmitting(true);
      const captchaTokenForRequest =
        requiresCaptcha && config.recaptcha_site_key ? currentCaptchaToken : "";
      captchaTokenRef.current = "";
      setCaptchaToken("");
      const offsetMinutes = new Date().getTimezoneOffset() * -1;
      const offsetHours = offsetMinutes / 60;
      try {
        const result = await callApi("POST", "/v1/auth/login-customer", {
          userPrincipalName: state.username,
          password: passwordValue,
          timeZoneOffset: offsetHours,
          recaptchaToken: captchaTokenForRequest || undefined,
        });
        if (result?.data?.data?.customer?.token) {
          resetCaptchaState(true);
          showLoginModal(false);
          updateTokenStorage(result?.data?.data?.customer?.token);
          if (result?.data?.data?.customer?.refreshToken) {
            updateRefreshTokenStorage(
              result?.data?.data?.customer?.refreshToken,
            );
          }
          const urlParams = new URLSearchParams(window.location.search);
          const redirectPath = getSafeLoginRedirect(urlParams.get("redirect"));
          const customer = result?.data?.data?.customer;

          if (customer?.isGoAiUser) {
            if (customer.isTermsAccepted) {
              if (customer?.isGoAiBillingRequired) {
                navigate("/gomake-ai/billing");
              } else {
                navigate("/gomake-ai");
              }
            } else {
              setIsModalOpen(true);
            }
          } else if (customer.isTermsAccepted) {
            navigate(redirectPath);
          } else {
            setIsModalOpen(true);
          }
        } else {
          const apiError = result?.errors as
            | { requiresCaptcha?: boolean; isBlocked?: boolean }
            | undefined;
          const isBlocked =
            apiError?.isBlocked === true || result?.status === 403;

          if (isBlocked) {
            setRequiresCaptcha(false);
            setErrorMsg(
              result?.msg === LOCKOUT_MESSAGE
                ? t("login.tooManyFailedAttempts")
                : result?.msg || t("login.tooManyFailedAttempts"),
            );
          } else {
            setRequiresCaptcha(apiError?.requiresCaptcha === true);
            setErrorMsg(result?.msg || t("login.invalidCredentialToken"));
          }
        }
      } finally {
        setIsSubmitting(false);
        resetCaptchaState();
      }
    }
  }, [
    state,
    t,
    callApi,
    showLoginModal,
    navigate,
    setIsModalOpen,
    requiresCaptcha,
    captchaToken,
    isSubmitting,
    resetCaptchaState,
  ]);

  useEffect(() => {
    resetCaptchaState(true);
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setState((prev) => ({
        ...prev,
        username: emailParam || prev.username,
      }));
    }
  }, [resetCaptchaState]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onClickLogin();
    }
  };

  const inputs = useMemo(() => {
    return [
      {
        name: "username",
        label: "login.username",
        type: "text",
        placeholder: "login.username",
        required: true,
        key: "username",
      },
      {
        name: "password",
        label: "login.password",
        type: "password",
        placeholder: "Password",
        required: true,
        key: "password",
      },
    ];
  }, []);

  const getUserProfile = async () => {
    const res = await callApi("GET", "/v1/get-print-house-profile");
    if (res.success) {
      setUserProfile(res?.data?.data);
    }
  };

  return {
    inputs,
    errors,
    errorMsg,
    changeState,
    onClickLogin,
    getUserProfile,
    handleKeyPress,
    t,
    isModalOpen,
    setIsModalOpen,
    setIsTermsAccepted,
    state,
    passwordRef,
    passwordDefaultValue,
    isLocalhost,
    localDomain,
    changeLocalDomain,
    handleApplyDomain,
    requiresCaptcha,
    captchaKey,
    handleCaptchaChange,
    handleCaptchaExpire,
  };
};

export { useGomakeLogin };
